import * as React from "react";
import { useState } from "react";
import useApi from "@/hooks/useApi";
import LoadingSpinner from "@/components/LoadingSpinner";
import {
  PrimaryBlueButton,
  SecondaryBlueButton,
  Severity
} from "@velaro/velaro-component-lib";
import useToastAlert from "@/hooks/useToastAlert";

interface Props {
  onApply: (draft: any) => void;
  onClose: () => void;
}

/**
 * AI Survey Generator modal.
 * User describes a goal → admin /Surveys/ai-generate returns a draft survey →
 * draft is dropped into the existing survey editor (unsaved) for tweaking.
 */
export default function AiSurveyGenerateModal({ onApply, onClose }: Props) {
  const api = useApi();
  const toastAlert = useToastAlert();
  const [goal, setGoal] = useState("");
  const [targetLength, setTargetLength] = useState<number>(8);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generate = async () => {
    if (!goal.trim()) {
      setError("Please describe what you want this survey to learn.");
      return;
    }
    setError(null);
    setGenerating(true);
    try {
      const resp = await api.admin.post("Surveys/ai-generate", {
        goal: goal.trim(),
        targetLength
      });
      if (resp.status === 402) {
        setError(
          "AI Survey Builder isn't enabled on this plan. Contact your account manager to upgrade."
        );
        return;
      }
      if (!resp.ok) {
        const body = await resp.text().catch(() => "");
        setError(`AI generation failed (HTTP ${resp.status}). ${body.slice(0, 240)}`);
        return;
      }
      const draft = await resp.json();
      toastAlert.displayToast(
        Severity.Success,
        "Survey draft created — tweak and save."
      );
      onApply(draft);
    } catch (e: any) {
      setError(e?.message || "Network error.");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white dark:bg-stone-300 rounded-md shadow-xl w-full max-w-lg p-5 mx-4">
        <div className="flex items-center gap-2 mb-3">
          <svg
            className="w-5 h-5 text-primary"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M10 2l1.5 4.5L16 8l-4.5 1.5L10 14l-1.5-4.5L4 8l4.5-1.5L10 2z" />
          </svg>
          <h2 className="text-lg font-semibold">Generate Survey with AI</h2>
        </div>

        <p className="text-sm text-slate-600 mb-4">
          Describe what you want to learn from your visitors. The AI will draft
          questions; you can edit anything before saving.
        </p>

        <label className="block text-sm font-medium mb-1">
          Goal <span className="text-red-500">*</span>
        </label>
        <textarea
          className="input w-full mb-4"
          rows={4}
          placeholder='e.g. "Understand why customers cancel their subscription so we can improve retention."'
          value={goal}
          onChange={(e) => setGoal(e.target.value)}
          disabled={generating}
        />

        <label className="block text-sm font-medium mb-1">
          Target number of questions
        </label>
        <input
          type="number"
          className="input w-32 mb-4"
          min={3}
          max={25}
          value={targetLength}
          onChange={(e) =>
            setTargetLength(Math.max(3, Math.min(25, Number(e.target.value) || 8)))
          }
          disabled={generating}
        />

        {error && (
          <div className="mb-3 p-2 rounded bg-red-50 border border-red-200 text-red-700 text-sm">
            {error}
          </div>
        )}

        {generating && (
          <div className="mb-3">
            <LoadingSpinner inline />
            <span className="ml-2 text-sm text-slate-500">
              Drafting your survey…
            </span>
          </div>
        )}

        <div className="flex justify-end gap-2 mt-2">
          <SecondaryBlueButton
            label="Cancel"
            onClick={onClose}
            disabled={generating}
          />
          <PrimaryBlueButton
            label={generating ? "Generating…" : "Generate"}
            onClick={generate}
            disabled={generating}
          />
        </div>
      </div>
    </div>
  );
}
